import { useState, useEffect } from 'react';
import { FaUpload, FaTimes, FaPlus } from 'react-icons/fa';
import '../../styles/admin/ProductForm.css';

const ProductForm = ({ product, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    subcategory: '',
    colors: [],
    sizes: [],
    stock: '',
    image_url: '',
    additional_images: [],
    featured: false,
    visible: true,
    tags: [],
  });

  const [colorInput, setColorInput] = useState('');
  const [sizeInput, setSizeInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [additionalImageFiles, setAdditionalImageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Fetch categories and subcategories
    const fetchCategories = async () => {
      try {
        const response = await fetch('/api/admin/categories', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch categories');
        }

        const data = await response.json();
        setCategories(data);
      } catch (error) {
        // Error will be handled by the global error handler
      }
    };

    fetchCategories();

    // If editing existing product, populate form
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        category: product.category || '',
        subcategory: product.subcategory || '',
        colors: product.colors
          ? typeof product.colors === 'string'
            ? product.colors.split(',')
            : product.colors
          : [],
        sizes: product.sizes
          ? typeof product.sizes === 'string'
            ? product.sizes.split(',')
            : product.sizes
          : [],
        stock: product.stock || '',
        image_url: product.image_url || '',
        additional_images: product.additional_images || [],
        featured: product.featured || false,
        visible: product.visible !== undefined ? product.visible : true,
        tags: product.tags || [],
      });
    }
  }, [product]);

  // Update subcategories when category changes
  useEffect(() => {
    const category = categories.find(cat => cat.name === formData.category);
    if (category && category.subcategories) {
      setSubcategories(category.subcategories);
    } else {
      setSubcategories([]);
    }
  }, [formData.category, categories]);

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleColorAdd = () => {
    if (colorInput.trim() && !formData.colors.includes(colorInput.trim())) {
      setFormData(prev => ({
        ...prev,
        colors: [...prev.colors, colorInput.trim()],
      }));
      setColorInput('');
    }
  };

  const handleColorRemove = color => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.filter(c => c !== color),
    }));
  };

  const handleSizeAdd = () => {
    if (sizeInput.trim() && !formData.sizes.includes(sizeInput.trim())) {
      setFormData(prev => ({
        ...prev,
        sizes: [...prev.sizes, sizeInput.trim()],
      }));
      setSizeInput('');
    }
  };

  const handleSizeRemove = size => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.filter(s => s !== size),
    }));
  };

  const handleTagAdd = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleTagRemove = tag => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag),
    }));
  };

  const handleMainImageChange = e => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);

      // Preview image
      const reader = new FileReader();
      reader.onload = event => {
        setFormData(prev => ({
          ...prev,
          image_url: event.target.result,
        }));
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleAdditionalImageChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setAdditionalImageFiles(prev => [...prev, ...newFiles]);

      // Preview images
      const newPreviewUrls = [];

      newFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = event => {
          newPreviewUrls.push(event.target.result);

          if (newPreviewUrls.length === newFiles.length) {
            setFormData(prev => ({
              ...prev,
              additional_images: [...prev.additional_images, ...newPreviewUrls],
            }));
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleRemoveAdditionalImage = index => {
    setFormData(prev => ({
      ...prev,
      additional_images: prev.additional_images.filter((_, i) => i !== index),
    }));

    if (index < additionalImageFiles.length) {
      setAdditionalImageFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Price must be a positive number';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.stock || isNaN(formData.stock) || parseInt(formData.stock) < 0) {
      newErrors.stock = 'Stock must be a non-negative number';
    }
    if (!formData.image_url && !imageFile) {
      newErrors.image = 'Product image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setUploading(true);
      const finalData = { ...formData };

      // Handle image uploads if needed
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);

        const uploadResponse = await fetch('/api/admin/upload-image', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: imageFormData,
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload main image');
        }

        const uploadData = await uploadResponse.json();
        finalData.image_url = uploadData.url;
      }

      // Handle additional images upload
      if (additionalImageFiles.length > 0) {
        const additionalImagesFormData = new FormData();
        additionalImageFiles.forEach(file => {
          additionalImagesFormData.append('images', file);
        });

        const additionalUploadResponse = await fetch('/api/admin/upload-multiple-images', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: additionalImagesFormData,
        });

        if (!additionalUploadResponse.ok) {
          throw new Error('Failed to upload additional images');
        }

        const additionalUploadData = await additionalUploadResponse.json();

        // If editing, keep existing additional images that weren't removed
        if (product && product.additional_images) {
          const keptImages = product.additional_images.filter(url =>
            formData.additional_images.includes(url),
          );

          finalData.additional_images = [...keptImages, ...additionalUploadData.urls];
        } else {
          finalData.additional_images = additionalUploadData.urls;
        }
      }

      // Format data for API
      finalData.colors = finalData.colors.join(',');
      finalData.sizes = finalData.sizes.join(',');
      finalData.tags = finalData.tags.join(',');
      finalData.price = parseFloat(finalData.price);
      finalData.stock = parseInt(finalData.stock);

      onSave(finalData);
    } catch (error) {
      // Error will be handled by the global error handler
      setErrors({ submit: 'Failed to save product. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className='product-form-container'>
      <div className='form-header'>
        <h2>{product ? 'Edit Product' : 'Add New Product'}</h2>
        <button className='close-btn' onClick={onCancel}>
          &times;
        </button>
      </div>

      <form className='product-form' onSubmit={handleSubmit}>
        <div className='form-grid'>
          <div className='form-left'>
            <div className='form-group'>
              <label htmlFor='name'>Product Name *</label>
              <input
                type='text'
                id='name'
                name='name'
                value={formData.name}
                onChange={handleInputChange}
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <div className='error-message'>{errors.name}</div>}
            </div>

            <div className='form-group'>
              <label htmlFor='description'>Description *</label>
              <textarea
                id='description'
                name='description'
                value={formData.description}
                onChange={handleInputChange}
                className={errors.description ? 'error' : ''}
              />
              {errors.description && <div className='error-message'>{errors.description}</div>}
            </div>

            <div className='form-row'>
              <div className='form-group'>
                <label htmlFor='price'>Price ($) *</label>
                <input
                  type='number'
                  id='price'
                  name='price'
                  value={formData.price}
                  onChange={handleInputChange}
                  min='0'
                  step='0.01'
                  className={errors.price ? 'error' : ''}
                />
                {errors.price && <div className='error-message'>{errors.price}</div>}
              </div>

              <div className='form-group'>
                <label htmlFor='stock'>Stock *</label>
                <input
                  type='number'
                  id='stock'
                  name='stock'
                  value={formData.stock}
                  onChange={handleInputChange}
                  min='0'
                  className={errors.stock ? 'error' : ''}
                />
                {errors.stock && <div className='error-message'>{errors.stock}</div>}
              </div>
            </div>

            <div className='form-row'>
              <div className='form-group'>
                <label htmlFor='category'>Category *</label>
                <select
                  id='category'
                  name='category'
                  value={formData.category}
                  onChange={handleInputChange}
                  className={errors.category ? 'error' : ''}
                >
                  <option value=''>Select Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.category && <div className='error-message'>{errors.category}</div>}
              </div>

              <div className='form-group'>
                <label htmlFor='subcategory'>Subcategory</label>
                <select
                  id='subcategory'
                  name='subcategory'
                  value={formData.subcategory}
                  onChange={handleInputChange}
                  disabled={!formData.category || subcategories.length === 0}
                >
                  <option value=''>Select Subcategory</option>
                  {subcategories.map(subcategory => (
                    <option key={subcategory} value={subcategory}>
                      {subcategory}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className='form-group'>
              <label>Colors</label>
              <div className='input-with-button'>
                <input
                  type='text'
                  value={colorInput}
                  onChange={e => setColorInput(e.target.value)}
                  placeholder='e.g. Red, Blue, Green'
                />
                <button type='button' onClick={handleColorAdd}>
                  <FaPlus />
                </button>
              </div>

              <div className='tags-container'>
                {formData.colors.map(color => (
                  <div key={color} className='tag'>
                    <span>{color}</span>
                    <button type='button' onClick={() => handleColorRemove(color)}>
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className='form-group'>
              <label>Sizes</label>
              <div className='input-with-button'>
                <input
                  type='text'
                  value={sizeInput}
                  onChange={e => setSizeInput(e.target.value)}
                  placeholder='e.g. S, M, L, XL'
                />
                <button type='button' onClick={handleSizeAdd}>
                  <FaPlus />
                </button>
              </div>

              <div className='tags-container'>
                {formData.sizes.map(size => (
                  <div key={size} className='tag'>
                    <span>{size}</span>
                    <button type='button' onClick={() => handleSizeRemove(size)}>
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className='form-right'>
            <div className='form-group'>
              <label>Main Product Image *</label>
              <div className='main-image-uploader'>
                {formData.image_url ? (
                  <div className='main-image-preview'>
                    <img src={formData.image_url} alt='Product' />
                    <button
                      type='button'
                      onClick={() => {
                        setFormData(prev => ({ ...prev, image_url: '' }));
                        setImageFile(null);
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <div className='upload-placeholder'>
                    <FaUpload />
                    <p>Click to upload main product image</p>
                  </div>
                )}

                <input
                  type='file'
                  id='main-image'
                  accept='image/*'
                  onChange={handleMainImageChange}
                  className={errors.image ? 'error' : ''}
                />
              </div>
              {errors.image && <div className='error-message'>{errors.image}</div>}
            </div>

            <div className='form-group'>
              <label>Additional Images</label>
              <div className='additional-images-container'>
                {formData.additional_images.map((url, imageIndex) => (
                  <div key={url} className='additional-image'>
                    <img src={url} alt={`Product view ${imageIndex + 1}`} />
                    <button type='button' onClick={() => handleRemoveAdditionalImage(imageIndex)}>
                      <FaTimes />
                    </button>
                  </div>
                ))}

                <div className='additional-image-uploader'>
                  <FaUpload />
                  <p>Upload</p>
                  <input
                    type='file'
                    accept='image/*'
                    multiple
                    onChange={handleAdditionalImageChange}
                  />
                </div>
              </div>
            </div>

            <div className='form-group'>
              <label>Tags (for search and filtering)</label>
              <div className='input-with-button'>
                <input
                  type='text'
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  placeholder='e.g. summer, casual, party'
                />
                <button type='button' onClick={handleTagAdd}>
                  <FaPlus />
                </button>
              </div>

              <div className='tags-container'>
                {formData.tags.map(tag => (
                  <div key={tag} className='tag'>
                    <span>{tag}</span>
                    <button type='button' onClick={() => handleTagRemove(tag)}>
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className='form-checkboxes'>
              <div className='checkbox-group'>
                <input
                  type='checkbox'
                  id='featured'
                  name='featured'
                  checked={formData.featured}
                  onChange={handleInputChange}
                />
                <label htmlFor='featured'>Featured Product</label>
              </div>

              <div className='checkbox-group'>
                <input
                  type='checkbox'
                  id='visible'
                  name='visible'
                  checked={formData.visible}
                  onChange={handleInputChange}
                />
                <label htmlFor='visible'>Visible in Store</label>
              </div>
            </div>
          </div>
        </div>

        {errors.submit && <div className='error-message submit-error'>{errors.submit}</div>}

        <div className='form-actions'>
          <button type='button' className='cancel-btn' onClick={onCancel}>
            Cancel
          </button>

          <button type='submit' className='save-btn' disabled={uploading}>
            {uploading ? 'Saving...' : product ? 'Update Product' : 'Add Product'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
